import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Trash2, Search, Loader2, FileText, Receipt, IndianRupee, RefreshCw, ScanBarcode, Settings2, ArrowUpDown, Download, MessageCircle } from 'lucide-react';
import { printInvoice } from '../lib/invoicePrint';
import { paymentBadge } from '../lib/paymentStatus';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChemistBillingTab({ token: propToken, vendorType, rxBarcode, onRxProcessed }) {
  const getHeaders = () => ({ Authorization: `Bearer ${propToken || localStorage.getItem('vendor_token')}` });
  const itemLabel = vendorType === 'lab' ? 'test' : vendorType === 'inventory' || vendorType === 'marketing' ? 'product' : 'medicine';

  const [tab, setTab] = useState('new-bill');
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(false);

  // New Bill State - Bill To details (patient / doctor / B2B client - name not compulsory)
  const [billToName, setBillToName] = useState('');
  const [billToClinicName, setBillToClinicName] = useState('');
  const [billToPhone, setBillToPhone] = useState('');
  const [billToAltPhone, setBillToAltPhone] = useState('');
  const [billToEmail, setBillToEmail] = useState('');
  const [billToGst, setBillToGst] = useState('');
  const [billToAddress, setBillToAddress] = useState('');
  const [billToShippingAddress, setBillToShippingAddress] = useState('');
  const [billToPermanentAddress, setBillToPermanentAddress] = useState('');
  const [billToIdCard, setBillToIdCard] = useState('');
  const [numCartons, setNumCartons] = useState('');
  const [deliveryMode, setDeliveryMode] = useState('');
  const [showMoreBillToFields, setShowMoreBillToFields] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [searchingClient, setSearchingClient] = useState(false);
  const [prescriptionBarcode, setPrescriptionBarcode] = useState('');
  const [productBarcode, setProductBarcode] = useState('');
  const [scanningProduct, setScanningProduct] = useState(false);
  const [doctorId, setDoctorId] = useState('');
  const [doctorName, setDoctorName] = useState('');
  const [fetchingDoctorDetails, setFetchingDoctorDetails] = useState(false);
  const [billItems, setBillItems] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('flat');
  const [gstPct, setGstPct] = useState(18);
  const [gstInclusive, setGstInclusive] = useState(false);
  const [notes, setNotes] = useState('');
  const [medSearch, setMedSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [newBillAmountPaid, setNewBillAmountPaid] = useState('');
  const [newBillPaymentMode, setNewBillPaymentMode] = useState('cash');
  const [newBillTransactionId, setNewBillTransactionId] = useState('');
  const [editingBillId, setEditingBillId] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [cancelDialogBill, setCancelDialogBill] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [exportingBills, setExportingBills] = useState(false);

  // Bills History State
  const [bills, setBills] = useState([]);
  const [billSearch, setBillSearch] = useState('');
  const [billDateFrom, setBillDateFrom] = useState('');
  const [billDateTo, setBillDateTo] = useState('');
  const [billSort, setBillSort] = useState('created_at:-1');
  const [billPaymentFilter, setBillPaymentFilter] = useState('');
  const [selectedBill, setSelectedBill] = useState(null);
  const [amountPaidInput, setAmountPaidInput] = useState('');
  const [paymentModeInput, setPaymentModeInput] = useState('cash');
  const [transactionIdInput, setTransactionIdInput] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);

  // Ledger State
  const [ledger, setLedger] = useState({ entries: [], summary: {}, filtered_summary: {} });
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledgerDoctorSearch, setLedgerDoctorSearch] = useState('');
  const [ledgerDateFrom, setLedgerDateFrom] = useState('');
  const [ledgerDateTo, setLedgerDateTo] = useState('');
  const [ledgerSort, setLedgerSort] = useState('created_at:-1');

  // Commission Config State
  const [commissionPct, setCommissionPct] = useState(0);
  const [perDoctorCommissions, setPerDoctorCommissions] = useState([]);
  const [linkedDoctors, setLinkedDoctors] = useState([]);
  const [recentClients, setRecentClients] = useState([]);
  const [savingCommission, setSavingCommission] = useState(false);

  // Bank Account Details State
  const [bankHolder, setBankHolder] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankIfsc, setBankIfsc] = useState('');
  const [bankName, setBankName] = useState('');
  const [savingBankDetails, setSavingBankDetails] = useState(false);
  const [loadingBankDetails, setLoadingBankDetails] = useState(false);

  const fetchMedicines = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/chemist/inventory?search=${medSearch}`, { headers: getHeaders() });
      setMedicines(res.data);
    } catch (err) { console.error(err); }
  }, [medSearch]);

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      const [sort_by, sort_order] = billSort.split(':');
      const params = new URLSearchParams({ sort_by, sort_order });
      if (billSearch) params.append('search', billSearch);
      if (billDateFrom) params.append('date_from', billDateFrom);
      if (billDateTo) params.append('date_to', billDateTo);
      if (billPaymentFilter) params.append('payment_status', billPaymentFilter);
      const res = await axios.get(`${API}/api/chemist/walkin-bills?${params}`, { headers: getHeaders() });
      setBills(res.data.bills || []);
    } catch { toast.error('Failed to load bills'); }
    setLoading(false);
  }, [billSearch, billDateFrom, billDateTo, billSort, billPaymentFilter]);

  const fetchLedger = useCallback(async () => {
    try {
      const [sort_by, sort_order] = ledgerSort.split(':');
      const params = new URLSearchParams({ sort_by, sort_order });
      if (ledgerSearch) params.append('search', ledgerSearch);
      if (ledgerDoctorSearch) params.append('doctor_search', ledgerDoctorSearch);
      if (ledgerDateFrom) params.append('date_from', ledgerDateFrom);
      if (ledgerDateTo) params.append('date_to', ledgerDateTo);
      const res = await axios.get(`${API}/api/chemist/walkin-ledger?${params}`, { headers: getHeaders() });
      setLedger(res.data);
    } catch (err) { console.error(err); }
  }, [ledgerSearch, ledgerDoctorSearch, ledgerDateFrom, ledgerDateTo, ledgerSort]);

  const fetchCommissionConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/chemist/commission-config`, { headers: getHeaders() });
      setCommissionPct(res.data.doctor_commission_percentage || 0);
      setPerDoctorCommissions(res.data.per_doctor_list || []);
    } catch (err) { console.error(err); }
  };

  const fetchLinkedDoctors = async () => {
    try {
      const res = await axios.get(`${API}/api/vendor-doctor-mappings/vendor/my-doctors`, { headers: getHeaders() });
      setLinkedDoctors((res.data || []).filter(d => d.status === 'approved'));
    } catch (err) { console.error(err); }
  };

  const fetchBankDetails = async () => {
    setLoadingBankDetails(true);
    try {
      const res = await axios.get(`${API}/api/b2b/vendors/me`, { headers: getHeaders() });
      setBankHolder(res.data.bank_account_holder || '');
      setBankAccountNumber(res.data.bank_account_number || '');
      setBankIfsc(res.data.bank_ifsc || '');
      setBankName(res.data.bank_name || '');
    } catch (err) { console.error(err); }
    setLoadingBankDetails(false);
  };

  const saveBankDetails = async () => {
    setSavingBankDetails(true);
    try {
      await axios.put(`${API}/api/b2b/vendors/me`, {
        bank_account_holder: bankHolder,
        bank_account_number: bankAccountNumber,
        bank_ifsc: bankIfsc,
        bank_name: bankName,
      }, { headers: getHeaders() });
      toast.success('Bank details saved - will appear on new bills going forward');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save bank details');
    }
    setSavingBankDetails(false);
  };

  const fetchRecentClients = async () => {
    try {
      const res = await axios.get(`${API}/api/chemist/recent-clients`, { headers: getHeaders() });
      setRecentClients(res.data || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchMedicines(); fetchCommissionConfig(); fetchLinkedDoctors(); fetchRecentClients(); fetchBankDetails(); }, [fetchMedicines]);

  useEffect(() => { if (tab === 'history') fetchBills(); }, [billSort, billPaymentFilter, tab]);
  useEffect(() => { if (tab === 'ledger') fetchLedger(); }, [ledgerSort, tab]);
  useEffect(() => {
    setAmountPaidInput(selectedBill ? String(selectedBill.amount_paid ?? 0) : '');
    setPaymentModeInput(selectedBill?.payment_mode || 'cash');
    setTransactionIdInput(selectedBill?.transaction_id || '');
  }, [selectedBill]);

  // Auto-scan prescription barcode from QR redirect
  useEffect(() => {
    if (rxBarcode && !scanning) {
      setTab('new-bill');
      lookupBarcode(rxBarcode);
      if (onRxProcessed) onRxProcessed();
    }
  }, [rxBarcode]);

  // Barcode Scan / Lookup
  const searchClients = async () => {
    if (!clientSearchQuery.trim() || clientSearchQuery.trim().length < 2) { toast.error('Type at least 2 characters'); return; }
    setSearchingClient(true);
    try {
      const res = await axios.get(`${API}/api/chemist/client-search?query=${encodeURIComponent(clientSearchQuery.trim())}`, { headers: getHeaders() });
      setClientSearchResults(res.data || []);
      if ((res.data || []).length === 0) toast.info('No matching patient/client found');
    } catch { toast.error('Search failed'); }
    setSearchingClient(false);
  };

  const selectClientResult = (client) => {
    setBillToName(client.name || '');
    setBillToPhone(client.phone || '');
    setBillToEmail(client.email || '');
    setBillToAddress(client.address || '');
    setClientSearchResults([]);
    setClientSearchQuery('');
    toast.success(`Auto-filled details for ${client.name}${client.source === 'immuski' ? ' (IMMUSKI)' : ''}`);
  };

  const selectDoctor = async (id, name) => {
    setDoctorId(id);
    setDoctorName(name || '');
    if (!id) return;
    setFetchingDoctorDetails(true);
    try {
      const res = await axios.get(`${API}/api/chemist/doctor-billing-details/${id}`, { headers: getHeaders() });
      setBillToName(res.data.name || name || '');
      setBillToClinicName(res.data.clinic_name || '');
      setBillToPhone(res.data.phone || '');
      setBillToEmail(res.data.email || '');
      setBillToGst(res.data.gst_number || '');
      setBillToAddress(res.data.address || '');
    } catch (err) { console.error(err); }
    setFetchingDoctorDetails(false);
  };

  const lookupBarcode = async (barcodeOverride) => {
    const bc = barcodeOverride || prescriptionBarcode.trim();
    if (!bc) { toast.error('Enter a prescription barcode'); return; }
    setPrescriptionBarcode(bc);
    setScanning(true);
    try {
      const res = await axios.get(`${API}/api/chemist/prescription-lookup/${bc}`, { headers: getHeaders() });
      if (res.data.found) {
        await selectDoctor(res.data.doctor_id || '', res.data.doctor_name || '');
        if (res.data.patient_name) setBillToName(res.data.patient_name);

        // Show doctor link status
        if (res.data.doctor_linked === false && res.data.doctor_name) {
          toast.info(`Dr. ${res.data.doctor_name} is not linked with your chemist. This will be treated as a normal walk-in sale (no referral charges).`, { duration: 5000 });
        }

        // Use AI-matched items if available
        const matchedItems = res.data.matched_items || [];
        const rawMeds = res.data.medications || [];

        if (matchedItems.length > 0) {
          const newItems = matchedItems.map(m => {
            if (m.matched && m.catalog_product) {
              const p = m.catalog_product;
              return {
                medicine_id: p.id,
                name: p.name,
                quantity: 1,
                mrp: p.mrp || 0,
                selling_price: p.mrp || 0,
                batch_number: '',
                expiry_date: '',
                match_confidence: m.match_confidence,
                prescribed_name: m.prescribed_name,
              };
            } else {
              return {
                medicine_id: '',
                name: m.prescribed_name || '',
                quantity: 1,
                mrp: 0,
                selling_price: 0,
                batch_number: '',
                expiry_date: '',
                match_confidence: 'none',
                prescribed_name: m.prescribed_name,
              };
            }
          }).filter(i => i.name);
          setBillItems(prev => [...prev, ...newItems]);
          const matched = newItems.filter(i => i.medicine_id).length;
          toast.success(`Prescription found! ${matched}/${newItems.length} items matched with your catalog. Doctor: ${res.data.doctor_name || 'N/A'}`);
        } else if (rawMeds.length > 0) {
          // Fallback: add raw medication names without prices
          const newItems = rawMeds.map(m => ({
            medicine_id: '',
            name: m.name || m.medicine || '',
            quantity: parseInt(m.quantity) || parseInt(m.duration) || 1,
            mrp: 0,
            selling_price: 0,
            batch_number: '',
            expiry_date: ''
          })).filter(i => i.name);
          setBillItems(prev => [...prev, ...newItems]);
          toast.success(`Prescription found! ${newItems.length} items added (prices need manual entry). Doctor: ${res.data.doctor_name || 'N/A'}`);
        } else {
          toast.success(`Prescription found! Doctor: ${res.data.doctor_name || 'N/A'}. No medications to add.`);
        }
      } else {
        toast.error('Prescription not found for this barcode');
      }
    } catch { toast.error('Failed to lookup prescription'); }
    setScanning(false);
  };

  const addItem = (med) => {
    if (billItems.find(i => i.medicine_id === med.id)) {
      setBillItems(prev => prev.map(i => i.medicine_id === med.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setBillItems(prev => [...prev, {
        medicine_id: med.id,
        name: med.name,
        quantity: 1,
        mrp: med.mrp || 0,
        selling_price: med.mrp || 0,
        batch_number: med.batch_number || '',
        expiry_date: med.batch_expiry || '',
        barcode: med.barcode || ''
      }]);
    }
  };

  const lookupProductBarcode = async () => {
    const bc = productBarcode.trim();
    if (!bc) { toast.error('Enter or scan a barcode'); return; }
    setScanningProduct(true);
    try {
      const res = await axios.get(`${API}/api/chemist/product-lookup/${bc}`, { headers: getHeaders() });
      addItem(res.data);
      toast.success(`Added: ${res.data.name}`);
      setProductBarcode('');
    } catch (err) { toast.error(err.response?.data?.detail || 'No product found with this barcode'); }
    setScanningProduct(false);
  };

  const updateItem = (idx, field, value) => {
    setBillItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const removeItem = (idx) => {
    setBillItems(prev => prev.filter((_, i) => i !== idx));
  };

  const shareOnWhatsApp = (bill) => {
    const digits = (bill.patient_phone || bill.billed_to?.phone || '').replace(/\D/g, '');
    if (!digits) { toast.error('No phone number on this bill to share with'); return; }
    const waPhone = digits.length === 10 ? `91${digits}` : digits;
    const link = `${window.location.origin}/invoice/${bill.id}`;
    const text = `Hi ${bill.patient_name || 'there'}, here is your invoice ${bill.invoice_number} from ${bill.vendor_name} for Rs.${bill.total?.toFixed(2)}. View/download: ${link}`;
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(text)}`, '_blank');
  };

  const savePayment = async () => {
    if (!selectedBill) return;
    const amt = parseFloat(amountPaidInput);
    if (isNaN(amt) || amt < 0) { toast.error('Enter a valid amount'); return; }
    setSavingPayment(true);
    try {
      const res = await axios.put(`${API}/api/chemist/walkin-bill/${selectedBill.id}/payment`, {
        amount_paid: amt,
        payment_mode: paymentModeInput,
        transaction_id: transactionIdInput,
      }, { headers: getHeaders() });
      const updated = { ...selectedBill, amount_paid: res.data.amount_paid, balance_due: res.data.balance_due, payment_status: res.data.payment_status, payment_mode: res.data.payment_mode, transaction_id: res.data.transaction_id };
      setSelectedBill(updated);
      setBills(prev => prev.map(b => b.id === updated.id ? updated : b));
      toast.success(`Payment updated - status: ${res.data.payment_status}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update payment'); }
    setSavingPayment(false);
  };

  const subtotal = billItems.reduce((s, i) => s + i.selling_price * i.quantity, 0);
  const discountAmount = discountType === 'percentage' ? Math.round(subtotal * discount / 100 * 100) / 100 : discount;
  const taxable = subtotal - discountAmount;
  const gstAmount = gstInclusive
    ? Math.round((taxable - (taxable / (1 + gstPct / 100))) * 100) / 100
    : Math.round(taxable * gstPct / 100 * 100) / 100;
  const total = gstInclusive ? Math.round(taxable * 100) / 100 : Math.round((taxable + gstAmount) * 100) / 100;

  const resetBillForm = () => {
    setBillToName(''); setBillToClinicName(''); setBillToPhone(''); setBillToAltPhone('');
    setBillToEmail(''); setBillToGst(''); setBillToAddress(''); setBillToShippingAddress('');
    setBillToPermanentAddress(''); setBillToIdCard(''); setNumCartons(''); setDeliveryMode('');
    setBillItems([]); setDiscount(0); setNotes(''); setGstInclusive(false);
    setPrescriptionBarcode(''); setDoctorId(''); setDoctorName('');
    setNewBillAmountPaid(''); setNewBillPaymentMode('cash'); setNewBillTransactionId('');
    setEditingBillId(null); setEditReason('');
  };

  const startEditBill = (bill) => {
    setBillToName(bill.billed_to?.name || bill.patient_name || '');
    setBillToClinicName(bill.billed_to?.clinic_name || '');
    setBillToPhone(bill.billed_to?.phone || bill.patient_phone || '');
    setBillToAltPhone(bill.billed_to?.alternate_phone || '');
    setBillToEmail(bill.billed_to?.email || '');
    setBillToGst(bill.billed_to?.gst_number || '');
    setBillToAddress(bill.billed_to?.address || '');
    setBillToShippingAddress(bill.billed_to?.shipping_address || '');
    setBillToPermanentAddress(bill.billed_to?.permanent_address || '');
    setBillToIdCard(bill.billed_to?.id_card_number || '');
    setNumCartons(String(bill.num_cartons || ''));
    setDeliveryMode(bill.delivery_mode || '');
    setBillItems(bill.items || []);
    setDiscount(bill.discount || 0);
    setDiscountType('flat');
    setGstPct(bill.gst_percentage ?? 18);
    setGstInclusive(!!bill.gst_inclusive);
    setNotes(bill.notes || '');
    setPrescriptionBarcode(bill.prescription_barcode || '');
    setDoctorId(bill.doctor_id || '');
    setEditingBillId(bill.id);
    setEditReason('');
    setTab('new-bill');
  };

  const createBill = async () => {
    if (billItems.length === 0) { toast.error('Add at least one item to the bill'); return; }
    if (editingBillId && !editReason.trim()) { toast.error('Enter a reason for editing this bill'); return; }
    setCreating(true);
    try {
      const payload = {
        patient_name: billToName,
        patient_phone: billToPhone,
        items: billItems,
        discount: discountAmount,
        gst_percentage: gstPct,
        gst_inclusive: gstInclusive,
        notes,
        prescription_barcode: prescriptionBarcode,
        doctor_id: doctorId,
        billed_to: {
          name: billToName,
          clinic_name: billToClinicName,
          phone: billToPhone,
          alternate_phone: billToAltPhone,
          email: billToEmail,
          gst_number: billToGst,
          address: billToAddress,
          shipping_address: billToShippingAddress,
          permanent_address: billToPermanentAddress,
          id_card_number: billToIdCard,
        },
        num_cartons: parseInt(numCartons) || 0,
        delivery_mode: deliveryMode,
      };
      let res;
      if (editingBillId) {
        res = await axios.put(`${API}/api/chemist/walkin-bill/${editingBillId}`, { ...payload, edit_reason: editReason }, { headers: getHeaders() });
        toast.success(`Bill updated: ${res.data.bill?.invoice_number}`);
      } else {
        res = await axios.post(`${API}/api/chemist/walkin-bill`, {
          ...payload,
          amount_paid: parseFloat(newBillAmountPaid) || 0,
          payment_mode: newBillPaymentMode,
          transaction_id: newBillTransactionId,
        }, { headers: getHeaders() });
        toast.success(`Bill created: ${res.data.bill?.invoice_number}`);
      }
      resetBillForm();
      fetchBills(); fetchLedger(); fetchRecentClients();
      setTab('history');
      setSelectedBill(res.data.bill);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save bill'); }
    setCreating(false);
  };

  const cancelBill = async () => {
    if (!cancelReason.trim()) { toast.error('Enter a reason for cancelling this bill'); return; }
    setCancelling(true);
    try {
      await axios.post(`${API}/api/chemist/walkin-bill/${cancelDialogBill.id}/cancel`, { reason: cancelReason }, { headers: getHeaders() });
      toast.success('Bill cancelled');
      setCancelDialogBill(null); setCancelReason('');
      if (selectedBill?.id === cancelDialogBill.id) setSelectedBill(null);
      fetchBills(); fetchLedger();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel bill'); }
    setCancelling(false);
  };

  const exportBills = async () => {
    setExportingBills(true);
    try {
      const res = await axios.get(`${API}/api/chemist/walkin-bills/export`, { headers: getHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'walkin_bills_export.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('Export failed'); }
    setExportingBills(false);
  };

  const saveCommission = async () => {
    setSavingCommission(true);
    try {
      await axios.put(`${API}/api/chemist/commission-config?doctor_commission_percentage=${commissionPct}`, {}, { headers: getHeaders() });
      toast.success('Default referral charges saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSavingCommission(false);
  };

  const saveDoctorCommission = async (doctorId, pct) => {
    try {
      await axios.put(`${API}/api/chemist/commission-config/doctor/${doctorId}?commission_pct=${pct}`, {}, { headers: getHeaders() });
      toast.success('Doctor referral charges updated');
      fetchCommissionConfig();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="chemist-billing-tab">
      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === 'history') fetchBills(); if (v === 'ledger') fetchLedger(); }}>
        <TabsList>
          <TabsTrigger value="new-bill" data-testid="new-bill-tab"><Receipt className="w-4 h-4 mr-1" /> New Bill</TabsTrigger>
          <TabsTrigger value="history" data-testid="bill-history-tab"><FileText className="w-4 h-4 mr-1" /> Bill History</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="walkin-ledger-tab"><IndianRupee className="w-4 h-4 mr-1" /> Walk-in Ledger</TabsTrigger>
          <TabsTrigger value="commission" data-testid="commission-config-tab"><Settings2 className="w-4 h-4 mr-1" /> Referral Charges</TabsTrigger>
          <TabsTrigger value="bank-details" data-testid="bank-details-tab"><IndianRupee className="w-4 h-4 mr-1" /> Bank Details</TabsTrigger>
        </TabsList>

        <TabsContent value="new-bill">
          <Card className="mb-4">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">Bill To (Patient / Doctor / B2B Client)</CardTitle>
              <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setShowMoreBillToFields(v => !v)} data-testid="toggle-more-billto-fields">
                {showMoreBillToFields ? 'Hide extra details' : 'More billing details'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentClients.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5" data-testid="recent-clients-chips">
                  <span className="text-xs text-slate-400">Recent:</span>
                  {recentClients.map((c, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => selectClientResult(c)}
                      className="text-xs px-2 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-colors"
                      data-testid={`recent-client-chip-${i}`}
                    >
                      {c.name || c.phone}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-2 relative">
                <Input placeholder="Search patient/client by name, phone or email (incl. IMMUSKI)" value={clientSearchQuery} onChange={(e) => setClientSearchQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchClients()} data-testid="client-search-input" />
                <Button size="sm" variant="outline" onClick={searchClients} disabled={searchingClient} data-testid="client-search-btn">
                  {searchingClient ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
                {clientSearchResults.length > 0 && (
                  <div className="absolute top-10 left-0 right-0 z-10 bg-white border rounded shadow-lg max-h-48 overflow-y-auto" data-testid="client-search-results">
                    {clientSearchResults.map((c, i) => (
                      <div key={c.id || i} className="p-2 text-xs hover:bg-slate-50 cursor-pointer border-b flex justify-between items-center" onClick={() => selectClientResult(c)} data-testid={`client-result-${i}`}>
                        <div>
                          <p className="font-medium">{c.name} {c.source === 'immuski' && <Badge variant="outline" className="text-xs ml-1 text-purple-600 border-purple-300">IMMUSKI</Badge>}</p>
                          <p className="text-slate-500">{c.phone} {c.email && `• ${c.email}`}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input placeholder="Name (optional)" value={billToName} onChange={(e) => setBillToName(e.target.value)} data-testid="walkin-patient-name" />
                <Input placeholder="Clinic / Business Name (for B2B)" value={billToClinicName} onChange={(e) => setBillToClinicName(e.target.value)} data-testid="walkin-clinic-name" />
                <div className="space-y-1">
                  <select
                    className="w-full h-9 text-sm border rounded px-2"
                    value={doctorId}
                    onChange={(e) => {
                      const val = e.target.value;
                      const doc = linkedDoctors.find(d => d.doctor_id === val);
                      selectDoctor(val, doc?.doctor_name || '');
                    }}
                    data-testid="walkin-doctor-select"
                  >
                    <option value="">Connected Doctor: None (regular walk-in / B2C)</option>
                    {linkedDoctors.map(doc => (
                      <option key={doc.doctor_id} value={doc.doctor_id}>{doc.doctor_name || doc.doctor_id}</option>
                    ))}
                  </select>
                </div>
              </div>

              {doctorId && (
                <div className="bg-blue-50 border border-blue-200 p-2 rounded text-xs">
                  <p className="font-medium text-blue-700">
                    {fetchingDoctorDetails ? 'Fetching doctor billing details...' : `Connected Doctor: ${doctorName || doctorId}`}
                  </p>
                  <p className="text-blue-500">Details below were auto-filled from the doctor's clinic profile - edit as needed. Referral charges are auto-calculated & the bill will show in the doctor's portal.</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input placeholder="Phone" value={billToPhone} onChange={(e) => setBillToPhone(e.target.value)} data-testid="walkin-phone" />
                <div className="flex gap-2">
                  <Input placeholder="Prescription Barcode" value={prescriptionBarcode} onChange={(e) => setPrescriptionBarcode(e.target.value)} data-testid="prescription-barcode-input" />
                  <Button size="sm" variant="outline" onClick={() => lookupBarcode()} disabled={scanning} data-testid="scan-barcode-btn">
                    {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanBarcode className="w-4 h-4" />}
                  </Button>
                </div>
                <Input placeholder="GSTIN (optional)" value={billToGst} onChange={(e) => setBillToGst(e.target.value)} data-testid="walkin-client-gst" />
              </div>

              {showMoreBillToFields && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-2 border-t" data-testid="walkin-more-billto-fields">
                  <Input placeholder="Alternate Phone" value={billToAltPhone} onChange={(e) => setBillToAltPhone(e.target.value)} data-testid="walkin-alt-phone" />
                  <Input placeholder="Email" value={billToEmail} onChange={(e) => setBillToEmail(e.target.value)} data-testid="walkin-email" />
                  <Input placeholder="ID Card Number (Aadhaar/PAN/etc.)" value={billToIdCard} onChange={(e) => setBillToIdCard(e.target.value)} data-testid="walkin-id-card" />
                  <Input placeholder="Billing Address" value={billToAddress} onChange={(e) => setBillToAddress(e.target.value)} className="md:col-span-3" data-testid="walkin-client-address" />
                  <Input placeholder="Shipping Address" value={billToShippingAddress} onChange={(e) => setBillToShippingAddress(e.target.value)} className="md:col-span-3" data-testid="walkin-shipping-address" />
                  <Input placeholder="Permanent Address" value={billToPermanentAddress} onChange={(e) => setBillToPermanentAddress(e.target.value)} className="md:col-span-3" data-testid="walkin-permanent-address" />
                  <Input placeholder="No. of Cartons" type="number" value={numCartons} onChange={(e) => setNumCartons(e.target.value)} data-testid="walkin-num-cartons" />
                  <select className="h-9 text-sm border rounded px-2 md:col-span-2" value={deliveryMode} onChange={(e) => setDeliveryMode(e.target.value)} data-testid="walkin-delivery-mode">
                    <option value="">Mode of Delivery (optional)</option>
                    <option value="self_pickup">Self Pickup</option>
                    <option value="hand_delivery">Hand Delivery</option>
                    <option value="courier">Courier</option>
                    <option value="own_transport">Own Transport / Logistics</option>
                  </select>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left: Item Search */}
            <div className="lg:col-span-1 space-y-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Add Items</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex gap-2">
                    <Input placeholder="Scan / enter barcode" value={productBarcode} onChange={(e) => setProductBarcode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && lookupProductBarcode()} data-testid="product-barcode-scan-input" />
                    <Button size="sm" variant="outline" onClick={lookupProductBarcode} disabled={scanningProduct} data-testid="scan-product-barcode-btn">
                      {scanningProduct ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanBarcode className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Input placeholder={`Search ${itemLabel}s...`} value={medSearch} onChange={(e) => setMedSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchMedicines()} />
                    <Button size="sm" variant="outline" onClick={fetchMedicines}><Search className="w-4 h-4" /></Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {medicines.map(med => (
                      <div key={med.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded text-xs border">
                        <div>
                          <p className="font-medium">{med.name}</p>
                          <p className="text-slate-500">MRP: {med.mrp} | Stock: {med.stock_quantity || 0}</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => addItem(med)}>
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right: Bill Items & Summary */}
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Bill Items ({billItems.length})</CardTitle></CardHeader>
                <CardContent>
                  {billItems.length === 0 ? (
                    <p className="text-center text-slate-400 py-6">Add items from the left panel or scan a prescription barcode</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b bg-slate-50">
                          <th className="text-left p-2">Item</th>
                          <th className="p-2 w-16">Qty</th>
                          <th className="p-2 w-24">Price</th>
                          <th className="p-2 w-24">Total</th>
                          <th className="p-2 w-10"></th>
                        </tr></thead>
                        <tbody>
                          {billItems.map((item, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="p-2">
                                {item.name}
                                {!item.medicine_id && <Badge variant="outline" className="ml-1 text-xs text-amber-600 border-amber-300">Manual</Badge>}
                                {item.match_confidence === 'high' && <Badge variant="outline" className="ml-1 text-xs text-green-600 border-green-300">AI Match</Badge>}
                                {item.match_confidence === 'medium' && <Badge variant="outline" className="ml-1 text-xs text-blue-600 border-blue-300">Similar</Badge>}
                                {item.match_confidence === 'low' && <Badge variant="outline" className="ml-1 text-xs text-orange-600 border-orange-300">Possible</Badge>}
                                {item.prescribed_name && item.prescribed_name !== item.name && (
                                  <span className="block text-xs text-slate-400">Rx: {item.prescribed_name}</span>
                                )}
                              </td>
                              <td className="p-2"><Input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)} className="h-7 w-16 text-xs" /></td>
                              <td className="p-2"><Input type="number" min="0" step="0.5" value={item.selling_price} onChange={(e) => updateItem(idx, 'selling_price', parseFloat(e.target.value) || 0)} className="h-7 w-24 text-xs" /></td>
                              <td className="p-2 font-medium">{(item.selling_price * item.quantity).toFixed(2)}</td>
                              <td className="p-2"><Button size="sm" variant="ghost" onClick={() => removeItem(idx)}><Trash2 className="w-3 h-3 text-red-500" /></Button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {billItems.length > 0 && (
                <Card>
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Discount</Label>
                        <select value={discountType} onChange={(e) => setDiscountType(e.target.value)} className="h-8 text-xs border rounded px-2">
                          <option value="flat">Flat (₹)</option>
                          <option value="percentage">Percent (%)</option>
                        </select>
                        <Input type="number" min="0" value={discount} onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)} className="h-8 w-20" />
                        {discountType === 'percentage' && <span className="text-xs text-slate-500">= ₹{discountAmount.toFixed(2)}</span>}
                      </div>
                      <div className="flex items-center gap-2"><Label className="text-xs">GST %</Label><Input type="number" min="0" value={gstPct} onChange={(e) => setGstPct(parseFloat(e.target.value) || 0)} className="h-8 w-20" /></div>
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="gst-inclusive-toggle">
                        <input type="checkbox" checked={gstInclusive} onChange={(e) => setGstInclusive(e.target.checked)} />
                        GST included in item price
                      </label>
                    </div>
                    <p className="text-xs text-slate-400">{gstInclusive ? 'Item prices already include GST - it will be shown separately but not added on top.' : 'GST will be added on top of the item prices.'}</p>
                    <Input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
                    <div className="border-t pt-3 space-y-1 text-sm">
                      <div className="flex justify-between"><span>Subtotal</span><span>{subtotal.toFixed(2)}</span></div>
                      <div className="flex justify-between text-red-500"><span>Discount {discountType === 'percentage' ? `(${discount}%)` : ''}</span><span>-{discountAmount.toFixed(2)}</span></div>
                      <div className="flex justify-between"><span>GST ({gstPct}%) {gstInclusive ? '(incl.)' : ''}</span><span>{gstAmount.toFixed(2)}</span></div>
                      <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total</span><span>{total.toFixed(2)}</span></div>
                    </div>
                    {editingBillId ? (
                      <div className="border-t pt-3 space-y-2">
                        <Label className="text-xs font-medium text-amber-600">Reason for editing this bill (required)</Label>
                        <Input placeholder="e.g. Corrected item quantity" value={editReason} onChange={(e) => setEditReason(e.target.value)} data-testid="edit-bill-reason-input" />
                      </div>
                    ) : (
                    <div className="border-t pt-3 space-y-2">
                      <Label className="text-xs font-medium">Payment Details</Label>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button type="button" size="sm" variant={parseFloat(newBillAmountPaid) > 0 ? 'outline' : 'default'} className="h-7 text-xs" onClick={() => setNewBillAmountPaid('0')} data-testid="new-bill-unpaid-btn">Unpaid</Button>
                        <Button type="button" size="sm" variant={parseFloat(newBillAmountPaid) === total && total > 0 ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setNewBillAmountPaid(String(total))} data-testid="new-bill-paid-full-btn">Paid in Full</Button>
                        <Input type="number" min="0" placeholder="Amount Paid" value={newBillAmountPaid} onChange={(e) => setNewBillAmountPaid(e.target.value)} className="h-7 w-28 text-xs" data-testid="new-bill-amount-paid-input" />
                        <select value={newBillPaymentMode} onChange={(e) => setNewBillPaymentMode(e.target.value)} className="h-7 text-xs border rounded px-2" data-testid="new-bill-payment-mode-select">
                          <option value="cash">Cash</option>
                          <option value="upi">UPI</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="card">Card</option>
                          <option value="other">Other</option>
                        </select>
                        {newBillPaymentMode !== 'cash' && (
                          <Input placeholder="Transaction ID" value={newBillTransactionId} onChange={(e) => setNewBillTransactionId(e.target.value)} className="h-7 w-36 text-xs" data-testid="new-bill-transaction-id-input" />
                        )}
                      </div>
                      <p className="text-xs text-slate-400" data-testid="new-bill-balance-due">Balance Due: ₹{Math.max(total - (parseFloat(newBillAmountPaid) || 0), 0).toFixed(2)}</p>
                    </div>
                    )}
                    <div className="flex gap-2">
                      {editingBillId && (
                        <Button variant="outline" className="flex-1" onClick={resetBillForm} data-testid="cancel-edit-bill-btn">Cancel Edit</Button>
                      )}
                      <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={createBill} disabled={creating} data-testid="create-walkin-bill-btn">
                        {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
                        {editingBillId ? 'Save Changes' : 'Generate Invoice'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap gap-2">
                <Input placeholder="Search patient/invoice..." value={billSearch} onChange={(e) => setBillSearch(e.target.value)} className="w-48 h-8 text-xs" data-testid="bill-history-search" />
                <Input type="date" value={billDateFrom} onChange={(e) => setBillDateFrom(e.target.value)} className="w-36 h-8 text-xs" />
                <Input type="date" value={billDateTo} onChange={(e) => setBillDateTo(e.target.value)} className="w-36 h-8 text-xs" />
                <select value={billSort} onChange={(e) => setBillSort(e.target.value)} className="h-8 text-xs border rounded px-2" data-testid="bill-history-sort">
                  <option value="created_at:-1">Newest First</option>
                  <option value="created_at:1">Oldest First</option>
                  <option value="total:-1">Total: High to Low</option>
                  <option value="total:1">Total: Low to High</option>
                </select>
                <select value={billPaymentFilter} onChange={(e) => setBillPaymentFilter(e.target.value)} className="h-8 text-xs border rounded px-2" data-testid="bill-history-payment-filter">
                  <option value="">All Payment Status</option>
                  <option value="paid">Paid</option>
                  <option value="partial">Partial</option>
                  <option value="unpaid">Unpaid</option>
                </select>
                <Button size="sm" variant="outline" onClick={fetchBills}><Search className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={exportBills} disabled={exportingBills} data-testid="export-bills-btn">
                  {exportingBills ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Export
                </Button>
              </div>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto">
              {loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div> :
              bills.length === 0 ? <p className="text-center text-slate-400 py-8">No bills found</p> : (
                <div className="space-y-2">
                  {bills.map(bill => (
                    <div key={bill.id} className={`p-3 border rounded-lg hover:bg-slate-50 cursor-pointer flex items-center justify-between ${bill.status === 'cancelled' ? 'opacity-60' : ''}`} onClick={() => setSelectedBill(bill)} data-testid={`bill-${bill.id}`}>
                      <div>
                        <p className="font-medium text-sm">{bill.invoice_number}</p>
                        <div className="text-xs text-slate-500">{bill.patient_name} {bill.prescription_barcode && <Badge variant="outline" className="text-xs ml-1">Rx Scan</Badge>} {bill.doctor_id && <Badge variant="outline" className="text-xs ml-1 text-teal-600 border-teal-300">Dr. Linked</Badge>} {bill.status === 'cancelled' && <Badge variant="outline" className="text-xs ml-1 text-red-600 border-red-300" data-testid={`bill-cancelled-badge-${bill.id}`}>Cancelled</Badge>}</div>
                        <p className="text-xs text-slate-400">{new Date(bill.created_at).toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">{bill.total?.toFixed(2)}</p>
                        <Badge className={`text-xs ${paymentBadge(bill.payment_status).className}`} data-testid={`bill-payment-badge-${bill.id}`}>{paymentBadge(bill.payment_status).label}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog open={!!selectedBill} onOpenChange={(o) => !o && setSelectedBill(null)}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader className="flex-row items-center justify-between pr-8 sticky top-0 bg-white z-10 pb-2 -mx-6 -mt-6 px-6 pt-6 border-b">
                <DialogTitle>Invoice: {selectedBill?.invoice_number}</DialogTitle>
                {selectedBill && (
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" className="text-green-600 border-green-300 hover:bg-green-50" onClick={() => shareOnWhatsApp(selectedBill)} data-testid="share-whatsapp-btn">
                      <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => printInvoice(selectedBill)} data-testid="download-invoice-btn">
                      <Download className="w-4 h-4 mr-1" /> Download
                    </Button>
                    {selectedBill.status !== 'cancelled' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => { startEditBill(selectedBill); setSelectedBill(null); }} data-testid="edit-bill-btn">
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => setCancelDialogBill(selectedBill)} data-testid="open-cancel-bill-btn">
                          Cancel Bill
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </DialogHeader>
              {selectedBill && (
                <div className="space-y-3 text-sm">
                  {selectedBill.status === 'cancelled' && (
                    <div className="bg-red-50 border border-red-200 p-2 rounded text-xs text-red-700" data-testid="invoice-cancelled-banner">
                      <strong>Cancelled</strong>{selectedBill.cancelled_at ? ` on ${new Date(selectedBill.cancelled_at).toLocaleString()}` : ''}
                      {selectedBill.cancel_reason && <div>Reason: {selectedBill.cancel_reason}</div>}
                    </div>
                  )}
                  <div className="border-b pb-2 flex items-start gap-3">
                    {selectedBill.vendor_logo && (
                      <img src={`${API}${selectedBill.vendor_logo}`} alt="Logo" className="w-12 h-12 object-contain rounded border" />
                    )}
                    <div>
                      <p className="font-bold">{selectedBill.vendor_name}</p>
                      <p className="text-xs text-slate-500">{selectedBill.vendor_address}</p>
                      <p className="text-xs text-slate-500">GST: {selectedBill.vendor_gst} | Ph: {selectedBill.vendor_phone}</p>
                    </div>
                  </div>
                  <div><strong>Bill To:</strong> {selectedBill.patient_name} {selectedBill.patient_phone && `| ${selectedBill.patient_phone}`}</div>
                  <div><strong>Date:</strong> {new Date(selectedBill.created_at).toLocaleString()}</div>
                  {selectedBill.billed_to && (selectedBill.billed_to.gst_number || selectedBill.billed_to.address || selectedBill.billed_to.clinic_name || selectedBill.billed_to.email) && (
                    <div className="bg-slate-50 p-2 rounded text-xs space-y-0.5">
                      <p className="font-medium">{selectedBill.billed_to.clinic_name || selectedBill.billed_to.name}</p>
                      {selectedBill.billed_to.gst_number && <p>GSTIN: {selectedBill.billed_to.gst_number}</p>}
                      {selectedBill.billed_to.email && <p>Email: {selectedBill.billed_to.email}</p>}
                      {selectedBill.billed_to.alternate_phone && <p>Alt. Phone: {selectedBill.billed_to.alternate_phone}</p>}
                      {selectedBill.billed_to.id_card_number && <p>ID: {selectedBill.billed_to.id_card_number}</p>}
                      {selectedBill.billed_to.address && <p>Billing Address: {selectedBill.billed_to.address}</p>}
                      {selectedBill.billed_to.shipping_address && <p>Shipping Address: {selectedBill.billed_to.shipping_address}</p>}
                      {selectedBill.billed_to.permanent_address && <p>Permanent Address: {selectedBill.billed_to.permanent_address}</p>}
                    </div>
                  )}
                  {(selectedBill.num_cartons > 0 || selectedBill.delivery_mode) && (
                    <div className="flex gap-3 text-xs text-slate-500">
                      {selectedBill.num_cartons > 0 && <span>Cartons: {selectedBill.num_cartons}</span>}
                      {selectedBill.delivery_mode && <span>Delivery: {selectedBill.delivery_mode.replace('_', ' ')}</span>}
                    </div>
                  )}
                  {selectedBill.prescription_barcode && <div><Badge variant="secondary" className="text-xs">Prescription: {selectedBill.prescription_barcode}</Badge></div>}
                  {selectedBill.notes && <div className="text-xs bg-amber-50 p-2 rounded" data-testid="invoice-notes"><strong>Notes:</strong> {selectedBill.notes}</div>}
                  <table className="w-full text-xs border"><thead><tr className="bg-slate-100"><th className="p-1 text-left">Item</th><th className="p-1">Qty</th><th className="p-1">Price</th><th className="p-1">Total</th></tr></thead><tbody>
                    {selectedBill.items?.map((item, i) => (
                      <tr key={`row-${i}`} className="border-t"><td className="p-1">{item.name}</td><td className="p-1 text-center">{item.quantity}</td><td className="p-1 text-right">{item.selling_price}</td><td className="p-1 text-right">{(item.selling_price * item.quantity).toFixed(2)}</td></tr>
                    ))}
                  </tbody></table>
                  <div className="border-t pt-2 space-y-1">
                    <div className="flex justify-between"><span>Subtotal</span><span>{selectedBill.subtotal?.toFixed(2)}</span></div>
                    {selectedBill.discount > 0 && <div className="flex justify-between text-red-500"><span>Discount</span><span>-{selectedBill.discount?.toFixed(2)}</span></div>}
                    <div className="flex justify-between"><span>GST ({selectedBill.gst_percentage}%){selectedBill.gst_inclusive ? ' (incl.)' : ''}</span><span>{selectedBill.gst_amount?.toFixed(2)}</span></div>
                    <div className="flex justify-between font-bold text-lg"><span>Total</span><span>{selectedBill.total?.toFixed(2)}</span></div>
                  </div>
                  <div className="border-t pt-2 space-y-2" data-testid="payment-section">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Payment Status</span>
                      <Badge className={paymentBadge(selectedBill.payment_status).className}>{paymentBadge(selectedBill.payment_status).label}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={amountPaidInput} onChange={(e) => setAmountPaidInput(e.target.value)} placeholder="Amount Paid" className="h-8 text-xs" data-testid="amount-paid-input" />
                      <select value={paymentModeInput} onChange={(e) => setPaymentModeInput(e.target.value)} className="h-8 text-xs border rounded px-1" data-testid="payment-mode-select">
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="card">Card</option>
                        <option value="cheque">Cheque</option>
                        <option value="other">Other</option>
                      </select>
                      <Button size="sm" onClick={savePayment} disabled={savingPayment} data-testid="save-payment-btn">
                        {savingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                    {paymentModeInput !== 'cash' && (
                      <Input value={transactionIdInput} onChange={(e) => setTransactionIdInput(e.target.value)} placeholder="Transaction ID / UTR / Reference No." className="h-8 text-xs" data-testid="transaction-id-input" />
                    )}
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Balance Due</span>
                      <span className="font-semibold text-slate-700">{(selectedBill.balance_due ?? (selectedBill.total - (selectedBill.amount_paid || 0))).toFixed(2)}</span>
                    </div>
                  </div>
                  {selectedBill.doctor_commission_amount > 0 && (
                    <div className="bg-blue-50 p-2 rounded text-xs">
                      <p className="font-medium text-blue-700">Referral Charges: Doctor {selectedBill.doctor_commission_amount} | Platform {selectedBill.admin_commission_amount}</p>
                    </div>
                  )}
                  {(selectedBill.vendor_bank_account_number || selectedBill.vendor_bank_name) && (
                    <div className="border-t pt-2 text-xs" data-testid="invoice-bank-details">
                      <p className="font-medium">Bank Account Details</p>
                      {selectedBill.vendor_bank_account_holder && <p>Account Holder: {selectedBill.vendor_bank_account_holder}</p>}
                      {selectedBill.vendor_bank_name && <p>Bank Name: {selectedBill.vendor_bank_name}</p>}
                      {selectedBill.vendor_bank_account_number && <p>Account Number: {selectedBill.vendor_bank_account_number}</p>}
                      {selectedBill.vendor_bank_ifsc && <p>IFSC Code: {selectedBill.vendor_bank_ifsc}</p>}
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={!!cancelDialogBill} onOpenChange={(o) => !o && (setCancelDialogBill(null), setCancelReason(''))}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Cancel Bill {cancelDialogBill?.invoice_number}</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-slate-500">This restores stock and reverses any commission entries for this bill. This action cannot be undone.</p>
              <Input placeholder="Reason for cancellation (required)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-bill-reason-input" />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setCancelDialogBill(null); setCancelReason(''); }}>Back</Button>
                <Button className="bg-red-600 hover:bg-red-700" onClick={cancelBill} disabled={cancelling} data-testid="confirm-cancel-bill-btn">
                  {cancelling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Confirm Cancel
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="ledger">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Walk-in Billing Ledger</CardTitle>
                <Button size="sm" variant="outline" onClick={fetchLedger}><RefreshCw className="w-4 h-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <Input placeholder="Search patient/invoice..." value={ledgerSearch} onChange={(e) => setLedgerSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchLedger()} className="w-40 h-8 text-xs" />
                <Input placeholder="Search doctor name..." value={ledgerDoctorSearch} onChange={(e) => setLedgerDoctorSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchLedger()} className="w-40 h-8 text-xs" data-testid="walkin-ledger-doctor-search" />
                <Input type="date" value={ledgerDateFrom} onChange={(e) => setLedgerDateFrom(e.target.value)} className="w-36 h-8 text-xs" />
                <Input type="date" value={ledgerDateTo} onChange={(e) => setLedgerDateTo(e.target.value)} className="w-36 h-8 text-xs" />
                <select value={ledgerSort} onChange={(e) => setLedgerSort(e.target.value)} className="h-8 text-xs border rounded px-2" data-testid="walkin-ledger-sort">
                  <option value="created_at:-1">Newest First</option>
                  <option value="created_at:1">Oldest First</option>
                  <option value="total:-1">Total: High to Low</option>
                  <option value="total:1">Total: Low to High</option>
                </select>
                <Button size="sm" variant="outline" onClick={fetchLedger}><ArrowUpDown className="w-4 h-4" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              {ledger.summary?.total_revenue > 0 && (
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-emerald-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-emerald-600">Revenue</p>
                    <p className="font-bold text-emerald-700">{ledger.summary.total_revenue?.toLocaleString()}</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-blue-600">Referral Charges Paid</p>
                    <p className="font-bold text-blue-700">{ledger.summary.total_commission?.toLocaleString()}</p>
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-amber-600">Net Amount</p>
                    <p className="font-bold text-amber-700">{ledger.summary.total_net?.toLocaleString()}</p>
                  </div>
                </div>
              )}
              {ledger.filtered_summary && (ledgerSearch || ledgerDoctorSearch) && ledger.filtered_summary.count > 0 && (
                <div className="mb-3 p-2.5 bg-violet-50 border border-violet-200 rounded-lg flex items-center gap-4 text-xs">
                  <span className="font-medium text-violet-700">{ledger.filtered_summary.count} entries</span>
                  <span className="text-slate-400">|</span>
                  <span>Revenue: <strong className="text-violet-700">₹{(ledger.filtered_summary.total_revenue || 0).toLocaleString()}</strong></span>
                  <span className="text-slate-400">|</span>
                  <span>Referral: <strong className="text-amber-600">₹{(ledger.filtered_summary.total_commission || 0).toLocaleString()}</strong></span>
                  <span className="text-slate-400">|</span>
                  <span>Net: <strong className="text-emerald-700">₹{(ledger.filtered_summary.total_net || 0).toLocaleString()}</strong></span>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-slate-50">
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Invoice</th>
                    <th className="text-left p-2">Patient</th>
                    <th className="text-left p-2">Ref. Doctor</th>
                    <th className="text-right p-2">Total</th>
                    <th className="text-right p-2">Referral Charges</th>
                    <th className="text-right p-2">Net</th>
                  </tr></thead>
                  <tbody>
                    {(ledger.entries || []).map(e => (
                      <tr key={e.id} className="border-b hover:bg-slate-50">
                        <td className="p-2 text-xs">{new Date(e.created_at).toLocaleDateString()}</td>
                        <td className="p-2 text-xs font-mono">{e.invoice_number}</td>
                        <td className="p-2 text-xs">{e.patient_name}</td>
                        <td className="p-2 text-xs text-teal-700 font-medium">{e.doctor_name || '-'}</td>
                        <td className="p-2 text-right">{e.total?.toFixed(2)}</td>
                        <td className="p-2 text-right text-red-500">{e.doctor_commission?.toFixed(2)}</td>
                        <td className="p-2 text-right font-medium">{e.net_amount?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!ledger.entries || ledger.entries.length === 0) && <p className="text-center text-slate-400 py-6">No walk-in billing records yet</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="commission">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Software Referral Charges Settings</CardTitle>
              <p className="text-xs text-slate-500 mt-1">Set a default referral charge rate, then optionally override for specific doctors.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-end gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Default Doctor Referral Charges %</Label>
                  <Input type="number" min="0" max="50" step="0.5" value={commissionPct} onChange={(e) => setCommissionPct(parseFloat(e.target.value) || 0)} className="w-32" data-testid="commission-pct-input" />
                </div>
                <Button onClick={saveCommission} disabled={savingCommission} data-testid="save-commission-btn">
                  {savingCommission ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Default
                </Button>
              </div>

              {/* Per-Doctor Overrides */}
              {linkedDoctors.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <Label className="text-xs font-semibold mb-2 block">Per-Doctor Referral Charge Overrides</Label>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {linkedDoctors.map(doc => {
                      const override = perDoctorCommissions.find(p => p.doctor_id === doc.doctor_id);
                      return (
                        <div key={doc.doctor_id} className="flex items-center justify-between p-2 border rounded text-sm" data-testid={`doctor-commission-${doc.doctor_id}`}>
                          <span className="font-medium text-xs">{doc.doctor_name || doc.doctor_id}</span>
                          <div className="flex items-center gap-2">
                            <Input type="number" min="0" max="50" step="0.5" placeholder={`${commissionPct}%`}
                              defaultValue={override?.commission_pct ?? ''}
                              className="w-20 h-7 text-xs"
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val) && val >= 0) saveDoctorCommission(doc.doctor_id, val);
                              }}
                            />
                            <span className="text-xs text-slate-400">%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="bg-slate-50 p-3 rounded-lg text-xs space-y-1">
                <p className="font-medium">How it works:</p>
                <p>Default rate (<strong>{commissionPct}%</strong>) applies to all doctors unless overridden above.</p>
                <p>Software referral charges are recorded on walk-in sales (with referring doctor), B2B orders, and patient portal orders.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bank-details">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Bank Account Details</CardTitle>
              <p className="text-xs text-slate-500">Shown on new invoices going forward (Bill History &gt; Download/Print). Existing bills are not updated retroactively.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {loadingBankDetails ? (
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Account Holder Name</Label>
                      <Input value={bankHolder} onChange={(e) => setBankHolder(e.target.value)} placeholder="e.g. City Pharmacy" data-testid="bank-holder-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Bank Name</Label>
                      <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. HDFC Bank" data-testid="bank-name-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Account Number</Label>
                      <Input value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)} placeholder="Account Number" data-testid="bank-account-number-input" />
                    </div>
                    <div>
                      <Label className="text-xs">IFSC Code</Label>
                      <Input value={bankIfsc} onChange={(e) => setBankIfsc(e.target.value.toUpperCase())} placeholder="e.g. HDFC0001234" data-testid="bank-ifsc-input" />
                    </div>
                  </div>
                  <Button onClick={saveBankDetails} disabled={savingBankDetails} data-testid="save-bank-details-btn">
                    {savingBankDetails ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                    Save Bank Details
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
